import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ComboBox } from "@carbon/react";
import {
  AssignableBuyer,
  useAssignableBuyers,
  useReassignItemBuyer,
} from "../../../hooks/usePurchaseRequestsV2";
import { useNotification, useUser } from "../../../context";
import { PurchaseRequestItemV2 } from "../../../types";
import { ABILITIES } from "../../../utils";

export interface AssignedBuyerFieldProps {
  requestId: string | undefined;
  item: PurchaseRequestItemV2;
}

const label = (buyer: AssignableBuyer | null | undefined) =>
  [buyer?.firstName, buyer?.lastName].filter(Boolean).join(" ") ||
  buyer?.email ||
  "";

/**
 * The item's buyer, and — for whoever may launch orders — a picker to reassign it. A SAP SOLPED's
 * buyer comes from the purchasing group, which is often stale, so buyers fix it here. Options are
 * the request company's active members (`/assignable-buyers`); clearing the box unassigns.
 */
const AssignedBuyerField = ({ requestId, item }: AssignedBuyerFieldProps) => {
  const { t } = useTranslation();
  const { can } = useUser();
  const { openNotification } = useNotification();
  const canReassign = can(ABILITIES.CREATE_ORDERS);
  const [buyerId, setBuyerId] = useState<string | null>(
    item.assignedBuyer?.id ?? null
  );

  useEffect(() => setBuyerId(item.assignedBuyer?.id ?? null), [item]);

  const buyers = useAssignableBuyers(canReassign ? requestId : undefined);
  const reassign = useReassignItemBuyer(
    requestId,
    (res) => {
      setBuyerId(res.assignedBuyer?.id ?? null);
      openNotification(t("purchaseRequests.items.buyerReassigned"));
    },
    () =>
      openNotification(t("purchaseRequests.items.buyerReassignError"), "error")
  );

  if (!canReassign) {
    return (
      <>
        {item.assignedBuyer?.fullName ||
          t("purchaseRequests.items.unassignedBuyer")}
      </>
    );
  }

  const options = buyers.data ?? [];
  const selected = options.find((b) => b.id === buyerId) ?? null;

  return (
    <ComboBox<AssignableBuyer>
      id={`assigned-buyer-${item.id}`}
      size="sm"
      items={options}
      itemToString={label}
      selectedItem={selected}
      placeholder={
        item.assignedBuyer?.fullName ||
        t("purchaseRequests.items.unassignedBuyer")
      }
      aria-label={t("purchaseRequests.filters.assignedBuyer")}
      // The box shows the current buyer's name; opening it must list everyone, not just them.
      shouldFilterItem={({ item: buyer, inputValue }) =>
        !inputValue ||
        inputValue === label(selected) ||
        `${label(buyer)} ${buyer?.email ?? ""}`
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      }
      disabled={buyers.isLoading || reassign.isPending}
      onChange={({ selectedItem }) => {
        const next = selectedItem?.id ?? null;
        if (next !== buyerId)
          reassign.mutate({ itemId: item.id, buyerId: next });
      }}
    />
  );
};

export default AssignedBuyerField;
